import { useCallback, useEffect, useRef } from "react";

/**
 * Reveal helper that prints a string into a state setter one character at a
 * time. Used by Prediction mode to "write" the AI answer into the note body
 * as if it had been there all along. ~22ms/char is a confident handwriting
 * rhythm — not so slow the cursor reads as a chat bot, not so fast it looks
 * pasted.
 *
 * @param {(updater: (prev: string) => string) => void} setText - the target text setter (accepts updater fn)
 * @param {{ intervalMs?: number }} opts
 * @returns {{ run: (text: string, opts?: { append?: boolean }) => void, cancel: () => void }}
 */
export default function useTypewriter(setText, { intervalMs = 22 } = {}) {
  const timerRef = useRef(null);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Type `text` into the target. By default the previous content is wiped
   * (replace) so the answer "appears" alone — used by Prediction mode.
   * With `{ append: true }` the new text is APPENDED to the existing
   * content, character by character — used by Scroll-peek where the
   * answer is tucked far below the spectator's input.
   */
  const run = useCallback((text, { append = false } = {}) => {
    cancel();
    if (!text) return;
    let basePromise;
    if (append) {
      // We need the current value as a starting point. setText accepts an
      // updater function so we can read prev synchronously inside React.
      basePromise = new Promise((resolve) => {
        setText((prev) => { resolve(prev ?? ""); return prev ?? ""; });
      });
    } else {
      setText("");
      basePromise = Promise.resolve("");
    }
    basePromise.then((base) => {
      let i = 0;
      timerRef.current = setInterval(() => {
        i += 1;
        setText(base + text.slice(0, i));
        if (i >= text.length) cancel();
      }, intervalMs);
    });
  }, [cancel, intervalMs, setText]);

  // Auto-cleanup on unmount
  useEffect(() => cancel, [cancel]);

  return { run, cancel };
}
